import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import LookupPicker from '@/components/LookupPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Busy, Field, Panel } from '@/components/kit';
import { PickSelect, SwitchRow } from '@/components/inputs';
import { DISCOUNT_KINDS, DISCOUNT_PLANS } from '@/shared/constants';
import { fromLocalInput, idOf, toLocalInput } from '@/shared/data';
import { money, newCode, planName } from '@/shared/pricing';

export const DISCOUNT_STATES = ['Active', 'Inactive', 'Expired', 'Used up'];

export const DISCOUNT_STATE_STYLES = {
  Active: 'border-success-border bg-success-muted text-success',
  Inactive: 'border-border bg-muted text-muted-foreground',
  Expired: 'border-warning-border bg-warning-muted text-warning',
  'Used up': 'border-highlight/40 bg-highlight/15 text-highlight',
};

export const KIND_LABELS = { Percent: 'Percent off', Fixed: 'Fixed amount off' };
export const PLAN_SCOPE_LABELS = { All: 'Every plan', Demon: 'Demon only', Wraith: 'Wraith only' };

/** "15% off every plan", "$5 off Wraith". */
export function discountLabel(d) {
  const value = Number(d?.value_c || 0);
  const amount = d?.kind_c === 'Fixed' ? `${money(value)} off` : `${value}% off`;
  const scope = !d?.plan_c || d.plan_c === 'All' ? 'every plan' : planName(d.plan_c);
  return `${amount} ${scope}`;
}

export function discountState(d, now = Date.now()) {
  if (!d?.active_c) return 'Inactive';
  if (d.expires_at_c && new Date(d.expires_at_c).getTime() < now) return 'Expired';
  if (d.max_uses_c && Number(d.uses_c || 0) >= Number(d.max_uses_c)) return 'Used up';
  return 'Active';
}

export const usesLabel = (d) => `${Number(d?.uses_c || 0)} / ${d?.max_uses_c ? Number(d.max_uses_c) : '∞'}`;

export function discountToForm(d) {
  return {
    Name: d?.Name ?? '',
    kind_c: d?.kind_c || 'Percent',
    value_c: d?.value_c != null ? String(d.value_c) : '',
    plan_c: d?.plan_c || 'All',
    max_uses_c: d?.max_uses_c ? String(d.max_uses_c) : '',
    uses_c: String(d?.uses_c ?? 0),
    expires_at_c: toLocalInput(d?.expires_at_c),
    active_c: d ? !!d.active_c : true,
    customer_c: idOf(d?.customer_c),
    credit_used_c: String(d?.credit_used_c ?? 0),
  };
}

export const blankDiscount = () => ({ ...discountToForm(null), Name: newCode('', 8) });

function toPayload(form) {
  return {
    Name: form.Name.trim().toUpperCase(),
    kind_c: form.kind_c,
    value_c: Number(form.value_c),
    plan_c: form.plan_c,
    max_uses_c: form.max_uses_c === '' ? null : Math.max(1, Math.round(Number(form.max_uses_c))),
    uses_c: Math.max(0, Math.round(Number(form.uses_c || 0))),
    expires_at_c: fromLocalInput(form.expires_at_c),
    active_c: !!form.active_c,
    customer_c: form.customer_c || null,
    credit_used_c: Math.max(0, Number(form.credit_used_c || 0)),
  };
}

/** Full discount-code form for the New and Edit pages. `onSubmit` receives the write payload. */
export default function DiscountForm({ initial, onSubmit, saving, submitLabel = 'Save code', busyLabel = 'Saving…', cancelTo }) {
  const [form, setForm] = useState(initial);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  function submit(e) {
    e.preventDefault();
    if (!/^[A-Za-z0-9-]{3,24}$/.test(form.Name.trim())) {
      return toast.error('Codes are 3 to 24 letters, numbers or dashes');
    }
    const value = Number(form.value_c);
    if (!(value > 0)) return toast.error('Add how much the code takes off');
    if (form.kind_c === 'Percent' && value > 100) return toast.error('A percent code tops out at 100');
    if (form.max_uses_c !== '' && !(Number(form.max_uses_c) >= 1)) return toast.error('Max uses is 1 or more, or empty for no limit');
    return onSubmit(toPayload(form));
  }

  const preview = discountLabel({ kind_c: form.kind_c, value_c: form.value_c, plan_c: form.plan_c });

  return (
    <form onSubmit={submit}>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="The code" icon="TicketPercent" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
          <Field label="Code" htmlFor="dc-name" className="sm:col-span-2" hint="What customers type at checkout.">
            <div className="flex gap-2">
              <Input
                id="dc-name"
                value={form.Name}
                onChange={text('Name')}
                placeholder="HALLOWEEN"
                className="font-mono uppercase"
              />
              <Button type="button" variant="outline" onClick={() => set('Name')(newCode('', 8))} aria-label="Generate a random code">
                <ApperIcon name="Shuffle" size={16} />
              </Button>
            </div>
          </Field>
          <Field label="Kind" htmlFor="dc-kind">
            <PickSelect id="dc-kind" value={form.kind_c} onChange={set('kind_c')} values={DISCOUNT_KINDS} labels={KIND_LABELS} placeholder="Percent or fixed" />
          </Field>
          <Field label={form.kind_c === 'Fixed' ? 'Amount (USD)' : 'Percent'} htmlFor="dc-value">
            <Input
              id="dc-value"
              type="number"
              min="0"
              step={form.kind_c === 'Fixed' ? '0.01' : '1'}
              value={form.value_c}
              onChange={text('value_c')}
              placeholder={form.kind_c === 'Fixed' ? '5.00' : '15'}
            />
          </Field>
          <Field label="Applies to" htmlFor="dc-plan" className="sm:col-span-2" hint={`Preview: ${preview}`}>
            <PickSelect id="dc-plan" value={form.plan_c} onChange={set('plan_c')} values={DISCOUNT_PLANS} labels={PLAN_SCOPE_LABELS} placeholder="Which plans" />
          </Field>
        </Panel>

        <Panel title="Limits" icon="Gauge" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
          <Field label="Max uses" htmlFor="dc-max" hint="Empty for no limit.">
            <Input id="dc-max" type="number" min="1" step="1" value={form.max_uses_c} onChange={text('max_uses_c')} placeholder="50" />
          </Field>
          <Field label="Used so far" htmlFor="dc-uses" hint="Goes up at each checkout.">
            <Input id="dc-uses" type="number" min="0" step="1" value={form.uses_c} onChange={text('uses_c')} placeholder="0" />
          </Field>
          <Field label="Expires" htmlFor="dc-expires" className="sm:col-span-2" hint="Empty for never.">
            <Input
              id="dc-expires"
              type="datetime-local"
              value={form.expires_at_c}
              onChange={text('expires_at_c')}
              placeholder="2026-12-31T23:59"
            />
          </Field>
          <div className="sm:col-span-2">
            <SwitchRow
              id="dc-active"
              label="Active"
              hint="Switch off to stop the code working without deleting it."
              checked={form.active_c}
              onChange={set('active_c')}
            />
          </div>
        </Panel>

        <Panel title="Referral" icon="Users" className="lg:col-span-2" bodyClassName="grid gap-4 py-4 sm:grid-cols-2">
          <Field label="Referrer" htmlFor="dc-customer" hint="Set for a personal or referral code. Leave empty for a public promo.">
            <LookupPicker
              table="customers_c"
              searchField="Name"
              value={form.customer_c}
              onChange={set('customer_c')}
              placeholder="Search customers…"
              clearable
            />
          </Field>
          <Field label="Credit used (USD)" htmlFor="dc-credit" hint="Referral credit the referrer has already spent.">
            <Input
              id="dc-credit"
              type="number"
              min="0"
              step="0.01"
              value={form.credit_used_c}
              onChange={text('credit_used_c')}
              placeholder="0.00"
            />
          </Field>
        </Panel>
      </div>

      <div className="sticky bottom-0 z-10 mt-6 flex justify-end gap-2 border-t border-border bg-background/95 py-3 backdrop-blur">
        <Button type="button" variant="outline" asChild>
          <Link to={cancelTo}>Cancel</Link>
        </Button>
        <Button type="submit" disabled={saving}>
          <Busy busy={saving} label={submitLabel} busyLabel={busyLabel} />
        </Button>
      </div>
    </form>
  );
}
